import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { ReactSVG } from 'react-svg';
import { useModal } from 'mui-modal-provider';

import Analytics from '../../Services/AnalyticsService';
import { Icons } from '../../Themes';
import { Categories, postTypePlural } from '../../Utils/Types/Posts';
import { HtmlToMUIMapper } from '../../Utils';
import useSpace from '../../Hooks/useSpace';
import useCurrentUser from '../../Hooks/useCurrentUser';
import EntitySubscriptionModal from '../EntitySubscriptionModal';

function MessagingBloc({
  spaceSlug,
  postType,
  ctaLabel,
  ctaUrl,
  content = null,
}: {
  spaceSlug: string;
  postType: 'demand' | 'anomaly';
  content?: string | null;
  ctaLabel?: string | null;
  ctaUrl?: string | null;
}) {
  const { space } = useSpace();
  const { isLoggedIn } = useCurrentUser();
  const defaultUrl = `/${spaceSlug}/${postTypePlural(postType)}/new`;
  const { showModal } = useModal();
  const handleClick = (e: Event) => {
    if (!ctaLabel && isLoggedIn && !space.entity_subscription) {
      showModal(EntitySubscriptionModal, { customRedirectTo: defaultUrl });
      return e.preventDefault();
    }

    Analytics.track('Home', `${postType} - Create Post`);
  };

  const buttonText = ctaLabel || (
    <FormattedMessage id={`MESSAGING_BLOC.${postType.toUpperCase()}.REPORT_CTA`} />
  );

  return (
    <Card>
      <CardContent
        sx={{
          padding: 3,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <ReactSVG src={Icons.posts.types[postType]} />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" align="center" component="h3">
            <HtmlToMUIMapper content={content} />
          </Typography>
        </Box>

        <Button
          variant="outlined"
          color="primary"
          size="large"
          onClick={handleClick}
          component={Link}
          to={ctaUrl || defaultUrl}
        >
          {buttonText}
        </Button>
      </CardContent>
    </Card>
  );
}

MessagingBloc.propTypes = {
  spaceSlug: PropTypes.string.isRequired,
  postType: PropTypes.oneOf(Categories.MESSAGING).isRequired,
};

export default MessagingBloc;
